import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Alert,
  Avatar,
} from "@mui/material";
import {
  Instagram,
  Facebook,
  YouTube,
  WhatsApp,
  CheckCircle,
  Add,
  AutoAwesome,
  ArrowForward,
} from "@mui/icons-material";
import { useSmbOnboarding } from "../../../contexts/SmbOnboardingContext";
import {
  initSocialOAuth,
  generateStarterContent,
  listContent,
  approveContent,
  submitExpertReview,
} from "../../../api/smb.api";

const platforms = [
  { id: "instagram", label: "Instagram", icon: <Instagram />, color: "#E4405F" },
  { id: "facebook", label: "Facebook", icon: <Facebook />, color: "#1877F2" },
  { id: "youtube", label: "YouTube", icon: <YouTube />, color: "#FF0000" },
  { id: "whatsapp_business", label: "WhatsApp Business", icon: <WhatsApp />, color: "#25D366" },
];

const SocialMediaStep = ({ onComplete, businessId }) => {
  const { socialAccounts, refreshSocialAccounts, contentItems, refreshContent } = useSmbOnboarding();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState([]);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  useEffect(() => {
    if (businessId) {
      refreshSocialAccounts(businessId);
      refreshContent(businessId);
    }
  }, [businessId, refreshSocialAccounts, refreshContent]);

  const handleConnect = async (platformId, accountType = "business") => {
    setConnectingPlatform(platformId);
    try {
      const result = await initSocialOAuth(businessId, platformId, accountType);
      if (result?.status && result?.data?.oauth_url) {
        window.open(result.data.oauth_url, "_blank", "width=600,height=700");
      }
    } catch (err) {
      console.error("OAuth init error:", err);
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleGenerateContent = async () => {
    setIsGenerating(true);
    setGenerationProgress([]);
    try {
      const response = await generateStarterContent(businessId);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              setGenerationProgress((prev) => [...prev, data]);
            } catch {}
          }
        }
      }
      await refreshContent(businessId);
    } catch (err) {
      console.error("Content generation error:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async (contentId) => {
    await approveContent(businessId, contentId);
    await refreshContent(businessId);
  };

  const handleExpertReview = async (contentId) => {
    await submitExpertReview(businessId, contentId);
    await refreshContent(businessId);
  };

  const isConnected = (platformId) => {
    return socialAccounts.some((acc) => acc.platform === platformId);
  };

  const connectedCount = (platformId) => {
    return socialAccounts.filter((acc) => acc.platform === platformId).length;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Platform Connection */}
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Connect Your Social Media
        </Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 3 }}>
          Connect up to 2 accounts per platform (personal + business)
        </Typography>

        <Grid container spacing={2}>
          {platforms.map((platform) => (
            <Grid item xs={6} sm={3} key={platform.id}>
              <Card
                sx={{
                  bgcolor: isConnected(platform.id) ? "rgba(16, 185, 129, 0.1)" : "#141626",
                  border: `1px solid ${isConnected(platform.id) ? "#10B981" : "#282D51"}`,
                  borderRadius: 2,
                  textAlign: "center",
                  py: 2,
                }}
              >
                <Avatar sx={{ bgcolor: platform.color, mx: "auto", mb: 1, width: 48, height: 48 }}>
                  {platform.icon}
                </Avatar>
                <Typography variant="caption" sx={{ display: "block", color: "#fff", fontWeight: 500 }}>
                  {platform.label}
                </Typography>
                {isConnected(platform.id) ? (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 14 }} />}
                    label={`${connectedCount(platform.id)}/2`}
                    size="small"
                    sx={{ mt: 1, bgcolor: "#10B981", color: "#fff", fontSize: "0.7rem" }}
                  />
                ) : (
                  <Button
                    size="small"
                    startIcon={connectingPlatform === platform.id ? <CircularProgress size={14} /> : <Add />}
                    onClick={() => handleConnect(platform.id)}
                    disabled={connectingPlatform !== null}
                    sx={{ mt: 1, color: "#3B82F6", fontSize: "0.7rem", textTransform: "none" }}
                  >
                    Connect
                  </Button>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Content Generation */}
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          AI-Generated Content
        </Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 2 }}>
          We'll create 5 posts and 2 reels tailored to your business
        </Typography>

        {contentItems.length === 0 && !isGenerating && (
          <Button
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={handleGenerateContent}
            fullWidth
            sx={{
              py: 1.5,
              bgcolor: "#7C3AED",
              "&:hover": { bgcolor: "#6D28D9" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Generate Starter Content
          </Button>
        )}

        {isGenerating && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress sx={{ color: "#7C3AED", mb: 2 }} />
            <Typography sx={{ color: "#9CA3AF" }}>
              Generating content... ({generationProgress.length} items)
            </Typography>
          </Box>
        )}

        {contentItems.length > 0 && (
          <Grid container spacing={2}>
            {contentItems.map((item) => (
              <Grid item xs={12} sm={6} key={item.id}>
                <Card sx={{ bgcolor: "#141626", border: "1px solid #282D51", borderRadius: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Chip
                        label={item.content_type}
                        size="small"
                        sx={{
                          bgcolor: item.content_type === "reel" ? "#7C3AED" : "#3B82F6",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                      <Chip
                        label={item.status}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: "#282D51", color: "#9CA3AF", fontSize: "0.7rem" }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: "#E5E7EB", lineHeight: 1.5 }}>
                      {item.caption?.String?.substring(0, 120)}
                      {item.caption?.String?.length > 120 && "..."}
                    </Typography>
                  </CardContent>
                  {item.status === "draft" && (
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        onClick={() => handleApprove(item.id)}
                        sx={{ color: "#10B981", textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleExpertReview(item.id)}
                        sx={{ color: "#F59E0B", textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Expert Review
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Alert severity="info" sx={{ mt: 2, bgcolor: "rgba(59, 130, 246, 0.1)", color: "#93C5FD" }}>
          All AI-generated content will be reviewed before posting. You can approve instantly or submit for expert review (24-hour SLA).
        </Alert>
      </Paper>

      {/* Continue Button */}
      <Button
        variant="contained"
        endIcon={<ArrowForward />}
        onClick={onComplete}
        fullWidth
        sx={{
          py: 1.5,
          bgcolor: "#3B82F6",
          "&:hover": { bgcolor: "#2563EB" },
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
        }}
      >
        Continue to Website Setup
      </Button>
    </Box>
  );
};

export default SocialMediaStep;
